import { CommonModule, CurrencyPipe } from '@angular/common';
import type { Signal } from '@angular/core';
import { Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';
import { MarkdownModule } from 'ngx-markdown';
import type { Observable } from 'rxjs';
import { combineLatest, map, switchMap, tap } from 'rxjs';

import { CarouselComponent } from '@valk-nx/components/ui-carousel/src/lib/carousel';
import type { CarouselItem } from '@valk-nx/components/ui-carousel/src/lib/carousel.interface';
import { IconGridComponent } from '@valk-nx/components/ui-icon-grid/src/lib/icon-grid';
import type { IconGridItem } from '@valk-nx/components/ui-icon-grid/src/lib/icon-grid.interface';
import { IconListComponent } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.component';
import type { Icon } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import type { ImageInterface } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import type { Link } from '@valk-nx/components/ui-link/src/lib/link.interface';
import { ListGridComponent } from '@valk-nx/components/ui-list-grid/src/lib/list-grid';
import { MapComponent } from '@valk-nx/components/ui-map/src/lib/map';
import { MiniCarouselComponent } from '@valk-nx/components/ui-mini-carousel/src/lib/mini-carousel';
import { RatingComponent } from '@valk-nx/components/ui-rating/src/lib/rating';
import { ReadMoreComponent } from '@valk-nx/components/ui-read-more/src/lib/read-more';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import type { Language } from '@valk-nx/core/lib/core';
import { negativeHeader$ } from '@valk-nx/core/lib/core';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import type { MeetingRoomResponseData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';

import { MeetingRoomDetailRequestFormComponent } from './request-form/meeting-room-detail-request-form';

@Component({
	selector: 'sb-meeting-room-detail',
	templateUrl: './meeting-room-detail.component.html',
	imports: [
		SVG_ICON_IMPORTS,
		CarouselComponent,
		CommonModule,
		DynamicModule,
		IconGridComponent,
		IconListComponent,
		LinkComponent,
		ListGridComponent,
		MarkdownModule,
		MapComponent,
		MeetingRoomDetailRequestFormComponent,
		MiniCarouselComponent,
		RatingComponent,
		ReadMoreComponent,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
	],
})
export class MeetingRoomDetailComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly sbService = inject(StoryblokService);
	private readonly metadataService = inject(MetadataService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly currencyPipe = inject(CurrencyPipe);

	readonly hotelStore = inject(HotelStore);

	optionalFacilities = computed(() => {
		return this.meetingRoom()?.facilities?.optional.map((item) => {
			if (item.onRequest) {
				return `${item.name} (${this.translate.instant(
					'global.on-request',
				)})`;
			} else {
				const formattedPrice = this.currencyPipe.transform(
					item.price / 100,
					this.hotelStore.currency(),
					'symbol-narrow',
					'1.2-2',
					this.translate.getCurrentLang(),
				);
				return `${item.name} (${formattedPrice} ${this.translate.instant(
					'global.apiece',
				)})`;
			}
		});
	});

	ctaLink = computed<Link | null>(() => {
		const customUrl = this.meetingRoom()?.customUrl;
		return customUrl ? { url: customUrl, target: '_blank' } : null;
	});

	showForm$: Observable<boolean>;
	meetingRoom$: Observable<MeetingRoomResponseData>;

	meetingRoom: Signal<MeetingRoomResponseData>;
	images: CarouselItem[] = [];
	hotelImages: ImageInterface[] = [];
	meetingRoomFacilityIconList: Icon[] = [];

	iconGridItems: IconGridItem[] = [];
	parentUrl = '';
	requestFormRoute = '';

	slugLanguage: Language;

	constructor() {
		super();
		negativeHeader$.next(true);

		this.showForm$ = this.route.fragment.pipe(
			map((fragment) => fragment === 'form'),
			tap((showForm) => {
				negativeHeader$.next(!showForm);
			}),
		);

		this.meetingRoom$ = combineLatest([
			this.sbService.translatedSlugsForCurrentStory(),
			this.showForm$,
		]).pipe(
			takeUntilDestroyed(),
			map(([translatedSlugs, showForm]) => {
				const slug =
					translatedSlugs.find((x) => x.selected) ??
					translatedSlugs[0];
				this.slugLanguage = slug.lang.toLowerCase() as Language;
				this.parentUrl = slug.path.substring(
					0,
					slug.path.lastIndexOf('/') + 1,
				);
				return { slug, showForm };
			}),
			switchMap(({ slug, showForm }) =>
				this.contentService
					.getMeetingRoom(
						slug.path.split('/').pop(),
						slug.lang.toLowerCase(),
					)
					.pipe(map((response) => ({ response, showForm }))),
			),
			map(({ response, showForm }) => {
				const meetingRoomData = response.data;
				this.metadataService.updateMetaDataBatch(
					meetingRoomData,
					this.robotsNoFollow(),
					this.robotsNoIndex(),
				);

				this.images = meetingRoomData.images.map((image) => {
					return {
						image: {
							src: image.src,
							alt: image.alt,
						},
						heading: null,
						subHeading: null,
						link: null,
					};
				});

				this.hotelImages = meetingRoomData.hotel.images?.map(
					(image) => {
						return {
							src: image.src,
							altText: image.alt,
							width: 0,
							height: 0,
						};
					},
				);

				this.meetingRoomFacilityIconList =
					meetingRoomData.surfaceArea && [
						{
							iconName: 'icon-surface-area',
							title: `${meetingRoomData.surfaceArea / 10000}m²`,
						},
					];

				this.iconGridItems = meetingRoomData?.layouts.map((layout) => {
					return {
						icon: `assets/icons/layout-${layout?.icon}.svg`,
						title: layout.name,
						subtitle:
							layout.maxPersons === 0
								? undefined
								: layout.maxPersons.toString(),
					};
				});

				/* v8 ignore start */
				if (showForm || this.images.length === 0) {
					negativeHeader$.next(false);
				} else {
					negativeHeader$.next(true);
				}
				/* v8 ignore stop */

				return meetingRoomData;
			}),
		);

		this.meetingRoom = toSignal(this.meetingRoom$);
	}

	onFormClosed(closed: boolean): void {
		if (closed) {
			this.router.navigate([], {
				relativeTo: this.route,
				fragment: undefined,
			});
		}
	}

	openForm(): void {
		this.router.navigate([], {
			relativeTo: this.route,
			fragment: 'form',
		});
	}
}
